/**
 * Left-edge vertical rail of dataset buttons - one per `uploads/<id>/`
 * folder (see ftgs/dataset-config.ts). Each button shows that dataset's own
 * reference mp4 as its thumbnail (a muted, autoplay-off `<video
 * preload="metadata">` - the browser paints the first frame on its own, no
 * separate thumbnail image asset needed). No visible text label
 * (user-requested - the thumbnail alone is enough to tell datasets apart;
 * the V3C/FreeTimeGS type badge was dropped since V3C won't be
 * implemented, leaving nothing left to distinguish) - `dataset.label` still drives the
 * button's `title`/`aria-label` so it's not lost for hover tooltips or
 * screen readers.
 *
 * Owns no dataset-loading logic itself - purely a view, same pattern as
 * FtgsPlaybackBar. main.ts wires `onSelect` to actually tear down/reload the
 * live scene, and calls `setActive`/`setLoading` to reflect state back.
 *
 * Reveal-on-edge-hover (user-requested): hidden (faded out, pointer-events
 * disabled) by default, fades in once the mouse comes within
 * EDGE_TRIGGER_PX of the browser's left edge OR is directly over the rail
 * itself, and starts fading back out the instant it leaves both (the
 * "gradual" part is purely the CSS opacity transition below, not a JS
 * delay - no grace period before the fade begins). Same window-level
 * pointermove + stopPropagation pattern as FtgsPlaybackBar's idle auto-hide
 * (see its module doc for why a WINDOW listener, not this.dom's own, is
 * needed despite this.dom stopping pointermove from bubbling).
 *
 * Edge hint tab (user-requested): before this, nothing on screen betrayed
 * that a rail existed at all - you had to already know to push the cursor
 * against the left edge. A slim chevron tab now sits flush against that
 * edge and follows the PLAYBACK BAR's visibility cycle (visible while the
 * mouse is active anywhere, fading after the same idle delay), so the
 * moment the UI "wakes up" the left side advertises itself too. Three
 * effective states: idle (nothing), mouse-active (hint only), revealed
 * (rail only - the hint fades so the two never show together). The hint
 * lives on document.body, not inside this.dom, because this.dom is the
 * very element the hidden class fades out - and fullscreen targets
 * documentElement (see ftgs-playback-bar.ts) so body children still render
 * there. Hovering the hint reveals the rail through the ordinary window
 * pointermove edge check (the tab IS inside the edge zone); the click
 * handler exists for touch, where no pointermove precedes the tap.
 */

import { Container } from '@playcanvas/pcui';

import { createPointerMoveFilter } from './pointer-move-filter';
import { datasetBaseUrl } from '../ftgs/dataset-config';

import type { FtgsDatasetConfig } from '../ftgs/dataset-config';

/** how close to the left edge (px) the cursor must get to reveal the rail */
const EDGE_TRIGGER_PX = 100;

/**
 * how long the mouse must sit still before the edge hint fades out - kept
 * equal to FtgsPlaybackBar's IDLE_HIDE_DELAY_MS by convention (not a shared
 * import, to avoid coupling the two modules over one number) so the hint
 * and the playback bar appear/disappear as one visibility cycle
 */
const HINT_IDLE_HIDE_DELAY_MS = 2000;

/**
 * 레일에 보이는 데이터셋 버튼 수 (2026-11-04 사용자 요청: 맨 위 하나만 표시). 나머지 버튼도 만들고
 * 선택·활성 표시 로직은 그대로 두며 화면에서만 숨김 - 값을 늘리면 다시 보임.
 */
const VISIBLE_DATASET_COUNT = 1;

/**
 * 마우스가 브라우저 창 밖으로 나간 이벤트인지 (나간 곳 relatedTarget 이 없음). 터치는 손을 뗄 때마다
 * relatedTarget 없는 pointerout/leave 가 오므로 제외 - 탭으로 레일을 연 직후 닫히지 않게.
 */
const leftWindow = (event: PointerEvent) => !event.relatedTarget && event.pointerType !== 'touch';

// right-pointing chevron - the conventional "something is tucked away on
// this side" glyph, same stroke style as the playback bar's icon set
const HINT_CHEVRON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';

class FtgsDatasetPicker extends Container {
    private buttons = new Map<string, HTMLButtonElement>();
    private activeId: string | null = null;
    private loadingId: string | null = null;

    private revealed = false;
    private hoveringPicker = false;

    // edge hint tab state - see this class's module doc
    private hintDom: HTMLButtonElement;
    private mouseActive = false;
    private hintHideTimeoutId: number | null = null;

    /** caller-supplied handler - left null until main.ts wires it up */
    onSelect: ((id: string) => void) | null = null;

    constructor(datasets: FtgsDatasetConfig[], args: Record<string, unknown> = {}) {
        super({ ...args, id: 'ftgs-dataset-picker' });

        // starts hidden - see this class's module doc
        this.dom.classList.add('ftgs-dataset-picker-hidden');

        // sits over the 3D canvas, same reasoning as FtgsPlaybackBar - without
        // this, clicking/dragging a button would also orbit/pan the camera
        // underneath it
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        // window-level, not this.dom's - deliberately unaffected by the
        // stopPropagation above (see module doc). Skipped entirely while
        // directly hovering the picker itself: the buttons extend to
        // left+264px, well past EDGE_TRIGGER_PX, so without this guard
        // moving the cursor across the rail's own buttons would re-hide it
        // mid-hover the instant clientX passed 50px.
        // 위치가 그대로인 이동 신호(포인터 모양 변경 때 Windows 가 보냄)는 무시 - pointer-move-filter.ts
        const isRealMove = createPointerMoveFilter();
        const onWindowPointerMove = (event: PointerEvent) => {
            if (!isRealMove(event)) {
                return;
            }
            // any movement anywhere counts as mouse activity for the hint,
            // independent of the hoveringPicker guard below
            this.setMouseActive(true);
            this.scheduleHintHide();
            if (this.hoveringPicker) {
                return;
            }
            this.setRevealed(event.clientX <= EDGE_TRIGGER_PX);
        };
        window.addEventListener('pointermove', onWindowPointerMove);

        // touch fires little to no pointermove before a tap, so pointerdown
        // is what actually wakes the hint there (mirrors how a tap wakes the
        // playback bar's fade via the synthetic pointermove most browsers
        // emit - but not all do, hence an explicit listener)
        const onWindowPointerDown = () => {
            this.setMouseActive(true);
            this.scheduleHintHide();
        };
        window.addEventListener('pointerdown', onWindowPointerDown);

        // --- edge hint tab (see module doc) ---
        const hint = document.createElement('button');
        hint.type = 'button';
        hint.id = 'ftgs-dataset-picker-hint';
        hint.className = 'ftgs-dataset-picker-hint-hidden';
        // black hover bubble rather than a native `title` popup: the OS draws
        // that outside the page, so the stereo split view's copy of this tab
        // (ui/ftgs-stereo-mirror.ts) could never show it in the other eye
        hint.dataset.tooltip = 'Datasets';
        hint.setAttribute('aria-label', 'Show dataset picker');
        hint.innerHTML = HINT_CHEVRON_SVG;
        // touch has no hover/pointermove leading up to the tap, so a tap on
        // the tab is the reveal path there; on mouse this is redundant with
        // the edge-zone check but harmless
        hint.addEventListener('click', () => this.setRevealed(true));
        document.body.appendChild(hint);
        this.hintDom = hint;

        // start the countdown immediately so a page load with no mouse
        // movement at all still ends up fully idle (same as the playback bar)
        this.scheduleHintHide();

        this.dom.addEventListener('pointerenter', () => {
            this.hoveringPicker = true;
            this.setRevealed(true);
        });
        this.dom.addEventListener('pointerleave', (event: PointerEvent) => {
            this.hoveringPicker = false;
            // re-check against the edge zone immediately using this event's
            // own coordinates, rather than waiting for the next window
            // pointermove (which won't fire again if the cursor leaves
            // straight into a stationary state, e.g. a menu/devtools focus
            // grab) - avoids the rail staying stuck visible.
            // 창 밖으로 나간 경우(relatedTarget 없음)는 좌표와 상관없이 닫음(2026-10-31): 레일이 왼쪽
            // 끝이라 밖으로 나가는 순간의 x 가 0 근처여서 열린 채 남았고, 그 뒤로는 이동 이벤트가 없음
            this.setRevealed(!leftWindow(event) && event.clientX <= EDGE_TRIGGER_PX);
        });

        // 레일을 거치지 않고 왼쪽 끝 영역에서 바로 창 밖으로 나가도 같은 이유로 남으므로, 창을
        // 벗어나는 순간 닫음. 입체 소프트웨어 커서가 켜져 있으면 실제 이벤트는 커서가 가로채고
        // 대신 위의 pointerleave(relatedTarget 없음)를 보내므로 그쪽에서 닫힘.
        const onWindowPointerOut = (event: PointerEvent) => {
            if (leftWindow(event) && !this.hoveringPicker) {
                this.setRevealed(false);
            }
        };
        window.addEventListener('pointerout', onWindowPointerOut);

        // 창이 다른 화면(글래스)으로 넘어가면 pointerleave·pointermove 가 오지 않아 레일이 열린 채,
        // 힌트가 보이는 채 남을 수 있음(2026-10-31) - 전체 화면이 바뀔 때 실제 호버 여부로 다시
        // 맞추고 힌트 타이머를 다시 걺
        const onFullscreenChange = () => {
            if (this.hoveringPicker && !this.dom.matches(':hover, .ftgs-stereo-hover')) {
                this.hoveringPicker = false;
            }
            if (!this.hoveringPicker) {
                this.setRevealed(false);
            }
            this.scheduleHintHide();
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);

        this.on('destroy', () => {
            window.removeEventListener('pointermove', onWindowPointerMove);
            window.removeEventListener('pointerdown', onWindowPointerDown);
            window.removeEventListener('pointerout', onWindowPointerOut);
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            this.clearHintHideTimer();
            this.hintDom.remove();
        });

        datasets.forEach((dataset, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'ftgs-dataset-button';
            // 표시만 숨김(인라인 style 이라 스테레오 복제 화면에도 그대로 복사됨) - VISIBLE_DATASET_COUNT
            if (index >= VISIBLE_DATASET_COUNT) {
                button.style.display = 'none';
            }
            // black hover bubble (same data-tooltip + ::after mechanism as
            // the stereo controls / stats rows) rather than the browser's own
            // `title` popup, which renders in the OS theme and ignores the
            // rest of the UI's styling. aria-label still carries the name for
            // assistive tech - data-tooltip is presentation only.
            button.dataset.tooltip = dataset.label;
            button.setAttribute('aria-label', dataset.label);

            const thumbWrap = document.createElement('div');
            thumbWrap.className = 'ftgs-dataset-thumb';

            if (dataset.thumbnail) {
                // still image (tens of KB) - replaced the old first-frame-
                // of-an-mp4 approach, which cost a multi-hundred-KB Range
                // fetch per dataset just to show one frozen frame
                const img = document.createElement('img');
                img.src = `${datasetBaseUrl(dataset.id)}/${dataset.thumbnail}`;
                img.alt = dataset.label;
                // deliberately NOT loading="lazy": the lazy trigger is tied
                // to paint/intersection timing, and this rail starts at
                // opacity:0 over a busy WebGPU canvas - on mobile the
                // trigger sometimes never fired at all, leaving black
                // buttons (reproduced: the request was never even issued;
                // flipping to eager loaded all three instantly). Three
                // ~40KB stills don't need deferral in the first place.
                img.draggable = false;
                thumbWrap.appendChild(img);
            } else if (dataset.video) {
                // legacy fallback for a dataset.json not yet migrated to
                // `thumbnail` (see dataset-config.ts's field doc)
                const video = document.createElement('video');
                video.src = `${datasetBaseUrl(dataset.id)}/${dataset.video}`;
                video.muted = true;
                video.playsInline = true;
                video.preload = 'metadata';
                // freeze on the first frame - this is a thumbnail, not a
                // preview player, so it deliberately never actually plays
                video.addEventListener('loadeddata', () => {
                    video.currentTime = 0;
                });
                thumbWrap.appendChild(video);
            }

            button.appendChild(thumbWrap);

            button.addEventListener('click', () => this.onSelect?.(dataset.id));

            this.buttons.set(dataset.id, button);
            this.dom.appendChild(button);
        });
    }

    /** the edge hint tab - lives on document.body, outside this.dom (see module doc) */
    get hintElement(): HTMLElement {
        return this.hintDom;
    }

    private setRevealed(revealed: boolean) {
        if (this.revealed === revealed) {
            return;
        }
        this.revealed = revealed;
        this.dom.classList.toggle('ftgs-dataset-picker-hidden', !revealed);
        this.updateHintVisibility();
    }

    private setMouseActive(active: boolean) {
        if (this.mouseActive === active) {
            return;
        }
        this.mouseActive = active;
        this.updateHintVisibility();
    }

    /** hint shows only in the middle state: mouse awake but rail not open */
    private updateHintVisibility() {
        const visible = this.mouseActive && !this.revealed;
        this.hintDom.classList.toggle('ftgs-dataset-picker-hint-hidden', !visible);
    }

    private clearHintHideTimer() {
        if (this.hintHideTimeoutId !== null) {
            window.clearTimeout(this.hintHideTimeoutId);
            this.hintHideTimeoutId = null;
        }
    }

    private scheduleHintHide() {
        this.clearHintHideTimer();
        this.hintHideTimeoutId = window.setTimeout(() => {
            this.hintHideTimeoutId = null;
            this.setMouseActive(false);
        }, HINT_IDLE_HIDE_DELAY_MS);
    }

    /** highlight the currently-loaded dataset's button */
    setActive(id: string) {
        if (this.activeId === id) {
            return;
        }
        if (this.activeId) {
            this.buttons.get(this.activeId)?.classList.remove('ftgs-dataset-button-active');
        }
        this.activeId = id;
        this.buttons.get(id)?.classList.add('ftgs-dataset-button-active');
    }

    /** shows a spinner-style state on the button whose data is currently loading (mid-teardown/reload) */
    setLoading(id: string | null) {
        if (this.loadingId) {
            this.buttons.get(this.loadingId)?.classList.remove('ftgs-dataset-button-loading');
        }
        this.loadingId = id;
        if (id) {
            this.buttons.get(id)?.classList.add('ftgs-dataset-button-loading');
        }
    }
}

export { FtgsDatasetPicker };
